import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AiIcon } from 'assets/icons/ai.svg';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-right.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as ProgressIcon } from 'assets/icons/progress.svg';
import { clsx } from 'clsx';
import {
  TAB_SCROLL_OFFSET,
  VALUE_CHAIN_AI_AGENT,
} from 'constants/general';

import type { TAICreatorBoMUnitsTabStatus } from '../../interfaces/IAICreatorBoMUnitsTab';
import type IBomUnitsTabs from './interfaces/IBomUnitsTabs';

import styles from './scss/BomUnitsTabs.module.scss';

function BomUnitsTabs({
  open,
  tabs,
  activeNodeId,
  onTabSelect,
}: IBomUnitsTabs) {
  const { t } = useTranslation();
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const [hasTabsOverflow, setHasTabsOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const syncTabsScrollState = useCallback(() => {
    const tabsContainer = tabsContainerRef.current;

    if (!tabsContainer) {
      setHasTabsOverflow(false);
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const nextHasOverflow = tabsContainer.scrollWidth > tabsContainer.clientWidth + 1;
    const nextCanScrollLeft = tabsContainer.scrollLeft > 0;
    const nextCanScrollRight = tabsContainer.scrollLeft + tabsContainer.clientWidth < tabsContainer.scrollWidth - 1;
    setHasTabsOverflow(nextHasOverflow);
    setCanScrollLeft(nextCanScrollLeft);
    setCanScrollRight(nextCanScrollRight);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const tabsContainer = tabsContainerRef.current;
    if (!tabsContainer) {
      return undefined;
    }

    syncTabsScrollState();

    tabsContainer.addEventListener('scroll', syncTabsScrollState);
    window.addEventListener('resize', syncTabsScrollState);

    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        syncTabsScrollState();
      });
      resizeObserver.observe(tabsContainer);
    }

    return () => {
      tabsContainer.removeEventListener('scroll', syncTabsScrollState);
      window.removeEventListener('resize', syncTabsScrollState);
      resizeObserver?.disconnect();
    };
  }, [open, syncTabsScrollState, tabs.length]);

  const handleScrollTabs = (direction: 'left' | 'right') => {
    const tabsContainer = tabsContainerRef.current;

    if (!tabsContainer) {
      return;
    }

    tabsContainer.scrollBy({
      left: direction === 'left' ? -TAB_SCROLL_OFFSET : TAB_SCROLL_OFFSET,
      behavior: 'smooth',
    });
  };

  const getTabStatusLabel = (status: TAICreatorBoMUnitsTabStatus): string => {
    if (status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT) {
      return t('valueChainMap.aiGeneratingStatusDone');
    }

    if (status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS) {
      return t('valueChainMap.aiGeneratingStatusInProgress');
    }

    if (status === VALUE_CHAIN_AI_AGENT.AI_STATUS) {
      return t('valueChainMap.aiGeneratingStatusAi');
    }

    return t('valueChainMap.aiGeneratingStatusEmpty');
  };

  const renderStatusIndicator = (status: TAICreatorBoMUnitsTabStatus) => {
    if (status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT) {
      return (
        <span className={clsx(styles.tabStatusIndicator, styles.tabStatusDone)} aria-hidden="true">
          <CheckIcon />
        </span>
      );
    }
    if (status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS) {
      return (
        <span className={clsx(styles.tabStatusIndicator, styles.tabStatusInProgress)} aria-hidden="true">
          <ProgressIcon />
        </span>
      );
    }

    if (status === VALUE_CHAIN_AI_AGENT.AI_STATUS) {
      return (
        <span className={clsx(styles.tabStatusIndicator, styles.tabStatusAi)} aria-hidden="true">
          <span className={styles.tabStatusAiTrack} />
          <span className={styles.tabStatusAiArc} />
          <AiIcon className={styles.tabStatusAiIcon} />
        </span>
      );
    }

    return <span className={clsx(styles.tabStatusIndicator, styles.tabStatusEmpty)} aria-hidden="true" />;
  };

  return (
    <div className={styles.tabsSection}>
      {hasTabsOverflow ? (
        <button
          type="button"
          className={clsx(styles.tabsScrollButton, styles.tabsScrollButtonLeft)}
          onClick={() => handleScrollTabs('left')}
          disabled={!canScrollLeft}
          aria-label={t('valueChainMap.bomUnitsScrollLeft')}
        >
          <ArrowIcon />
        </button>
      ) : null}
      <div className={styles.tabsScroller} ref={tabsContainerRef}>
        <div className={styles.tabs}>
          {tabs.map((tab) => {
            const isActive = tab.nodeId === activeNodeId;
            return (
              <button
                key={tab.nodeId}
                type="button"
                className={clsx(styles.tabButton, { [styles.tabButtonActive]: isActive })}
                onClick={() => onTabSelect(tab.nodeId)}
                aria-pressed={isActive}
                aria-label={`${tab.name}. ${getTabStatusLabel(tab.status)}`}
              >
                <span className={styles.tabTitle}>{tab.name}</span>
                {renderStatusIndicator(tab.status)}
              </button>
            );
          })}
        </div>
      </div>
      {hasTabsOverflow ? (
        <button
          type="button"
          className={styles.tabsScrollButton}
          onClick={() => handleScrollTabs('right')}
          disabled={!canScrollRight}
          aria-label={t('valueChainMap.bomUnitsScrollRight')}
        >
          <ArrowIcon />
        </button>
      ) : null}
    </div>
  );
}

export default BomUnitsTabs;
